import { FunctionComponent, useCallback, useMemo } from 'react'
import { SummaryAppointmentProps } from '.'
import { Avatar, Input, Modal, Text, Alert } from '@conexasaude/conexa-hero'
import { useSummaryAppointment } from './SummaryAppointment.hook'
import useStyles from './SummaryAppointment.styles'
import DivisorLine from '@/components/DivisorLine'
import { replaceValueNotExisting } from '@/utils/replaceValueNotExisting'
import { autoCapitalize } from '@/utils/autoCapitalize'
import RenderIf from '@/components/RenderIf'
import TextAreaControl from '@/components/Form/Rebranded/TextAreaControl'
import AutocompleteControl from '@/components/Form/Rebranded/AutocompleteControl'
import DatePickerControl from '@/components/Form/Rebranded/DatePickerControl'
import { subDays } from 'date-fns'
import { phoneReplace } from '@/utils/formatPhone'

export const SummaryAppointment: FunctionComponent<SummaryAppointmentProps> = ({
  summaryData,
  onCloseModal,
  ...rest
}) => {
  const styles = useStyles()

  const isAppointmentException = useMemo(() => {
    return summaryData?.appointmentObject?.key === 'AGENDAMENTO_POR_EXCECAO'
  }, [summaryData?.appointmentObject?.key])

  const {
    openModal,
    createAppointmentIsLoading,
    control,
    responsibleList,
    formExceptionDisabled,
    errors,
    fields,
    setFields,
    onSubmit,
    handleSubmit,
    emailValidate,
    phoneValidate,
    enableButton,
  } = useSummaryAppointment({
    isException: isAppointmentException,
    summaryData,
  })

  const handleCloseModal = useCallback(() => {
    onCloseModal()
  }, [onCloseModal])

  return (
    <form onSubmit={handleSubmit(() => onSubmit(summaryData))}>
      <Modal
        opened={Boolean(openModal)}
        title="Agendar consulta"
        onClose={handleCloseModal}
        primaryActionLabel="Sim, confirmar"
        primaryAction={handleSubmit(() => onSubmit(summaryData))}
        size={700}
        busyPrimaryAction={createAppointmentIsLoading}
        disabledPrimaryAction={
          (isAppointmentException && formExceptionDisabled) || !enableButton
        }
        {...rest}
      >
        <div css={styles.dataWrapper}>
          <Text variant="microCopy">Nome do paciente</Text>
          <Text variant="paragraphLarge" renderAs="p" css={styles.textColor}>
            {autoCapitalize(summaryData?.patient?.patientName)}
          </Text>

          <div>
            <Text variant="microCopy">Número da carteirinha</Text>
            <Text variant="paragraphLarge" renderAs="p" css={styles.textColor}>
              {summaryData?.healthInsuranceData?.healthInsuranceNumber || '-'}
            </Text>
          </div>

          <div css={styles.infoWrapper}>
            <div>
              <Text variant="microCopy">Objetivo do atendimento</Text>
              <Text
                variant="paragraphLarge"
                renderAs="p"
                css={styles.textColor}
              >
                {autoCapitalize(summaryData?.appointmentObject?.value)}
              </Text>
            </div>
            <div>
              <Text variant="microCopy">Atendimento de encaixe</Text>
              <Text
                variant="paragraphLarge"
                renderAs="p"
                css={styles.textColor}
              >
                {summaryData?.fitIn ? 'Sim' : 'Não'}
              </Text>
            </div>
            <div>
              <Text variant="microCopy">Data e horário de atendimento</Text>
              <Text
                variant="paragraphLarge"
                renderAs="p"
                css={styles.textColor}
              >
                {summaryData?.appointmentDate} - {summaryData?.appointmentHour}h
              </Text>
            </div>
          </div>
          <DivisorLine />

          <div css={styles.professionalWrapper}>
            <Avatar
              src={summaryData?.professionalPhotoUrl}
              alt={summaryData?.professional?.professionalName}
            />

            <div>
              <Text variant="paragraphLarge" css={styles.textColor}>
                {autoCapitalize(summaryData?.professional?.professionalName)}
              </Text>
              <Text variant="microCopy">
                {autoCapitalize(summaryData?.professionalClinic)}
              </Text>

              <div css={styles.detailsWrapper}>
                <RenderIf
                  condition={Boolean(summaryData?.professionalSpecialty)}
                >
                  <Text variant="microCopy">
                    {replaceValueNotExisting(
                      autoCapitalize(summaryData?.professionalSpecialty),
                    )}{' '}
                    |
                  </Text>
                </RenderIf>

                <Text variant="microCopy">
                  {summaryData?.councilType} - {summaryData?.councilNumber}
                </Text>
              </div>
            </div>
          </div>
          <RenderIf condition={isAppointmentException}>
            <DivisorLine />
            <Text variant="headingSmall" css={styles.titleWrapper}>
              Atendimento por exceção
            </Text>
            <div css={styles.footer}>
              <div css={styles.detailsInput}>
                <AutocompleteControl
                  label="Responsável pela autorização*"
                  placeholder="Selecione"
                  optionLabel="name"
                  name="responsible"
                  control={control}
                  options={responsibleList || []}
                  fullWidth
                />

                <DatePickerControl
                  name="exceptionDate"
                  label="Data da autorização*"
                  placeholder="Insira a data"
                  control={control}
                  minDate={subDays(new Date(), 7)}
                  maxDate={new Date()}
                  fullWidth
                />
              </div>

              <TextAreaControl
                label="Motivo da exceção*"
                name="exceptionReason"
                control={control}
                minRows={2}
                placeholder="Descreve o motivo da exceção"
                maxLength={2000}
                error={errors?.exceptionReason?.message?.toString()}
                fullWidth
              />
            </div>
          </RenderIf>
          <DivisorLine />

          <div>
            <Text variant="headingSmall" css={styles.titleWrapper}>
              Contatos do paciente
            </Text>
            <div css={styles.alertWrapper}>
              <Alert
                variant="warning"
                title="Certifique-se que o contato do paciente esteja atualizado e o informe que receberá a comunicação por e-mail para confirmar a consulta. A não confirmação em até 24 horas resultará no cancelamento do atendimento."
              />
            </div>
            <div css={styles.detailsInput}>
              <Input
                fullWidth
                label="E-mail*"
                value={fields?.patient?.patientEmail || ''}
                onChange={event => {
                  setFields(prevFields => ({
                    ...prevFields,
                    patient: {
                      ...prevFields?.patient,
                      patientEmail: event.target.value,
                    },
                  }))
                }}
                error={!emailValidate ? 'E-mail inválido' : ''}
              />
              <Input
                fullWidth
                label="Celular*"
                mask="(00) 00000-0000"
                value={phoneReplace(fields?.patient?.patientPhone || '')}
                onChange={event => {
                  setFields(prevFields => ({
                    ...prevFields,
                    patient: {
                      ...prevFields?.patient,
                      patientPhone: event.target.value,
                    },
                  }))
                }}
                error={!phoneValidate ? 'Celular incompleto ou inválido' : ''}
              />
            </div>
          </div>
        </div>
      </Modal>
    </form>
  )
}
